'use client';

import cn from 'classnames';
import { motion } from 'framer-motion';
import { InputHTMLAttributes, forwardRef, useId } from 'react';
import Icon from '../Icon';
import { Text } from '../Text';

export type InputFieldProps
  = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
    label?: string;
    errorMessage?: string;
    warningMessage?: string;
    helpMessage?: string;
    wrapperClassName?: string;
    loading?: boolean;
  }

/**
 * InputField is the themed text-input molecule used in rebrand demo / lead
 * forms. It renders a label, the native input styled with theme tokens
 * (`surface-l1`, `text-primary`, focus ring), and an inline error/warning
 * message slot.
 *
 * `errorMessage` takes precedence over `warningMessage` when both are set.
 */
export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      errorMessage,
      warningMessage,
      helpMessage,
      wrapperClassName,
      className,
      type = 'text',
      loading,
      ...inputProps
    },
    ref,
  ) => {
    const id = useId();
    const warningOrErrorMessage = errorMessage || warningMessage;

    return (
      <label htmlFor={id} className={cn('flex flex-col gap-2xs', wrapperClassName)}>
        {label && (
          <Text as="span" type="p2" className="text-primary">
            {label}
          </Text>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={id}
            type={type}
            className={cn(
              'w-full surface-l1 text-primary placeholder:text-primary-inactive',
              'rounded-[6px] px-s py-xs',
              'font-sans font-size-p2 font-line-height-p2',
              'outline-none focus-visible:ring-2 focus-visible:ring-citron-500',
              className,
            )}
            {...inputProps}
          />
          {loading && (
            <motion.div
              className="flex absolute right-1 top-0 bottom-0"
              style={{ transformOrigin: 'center center' }}
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1.75, ease: 'linear', repeat: Infinity }}
            >
              <Icon className="m-auto" name="settings-02" size={16} />
            </motion.div>
          )}
        </div>
        {/* Show help message OR warning/error message. */}
        {helpMessage && !warningOrErrorMessage && (
          <Text as="span" type="caption2" className="text-gray-500">
            {helpMessage}
          </Text>
        )}
        {warningOrErrorMessage && (
          <Text as="span" type="caption2" className="text-red-500">
            {warningOrErrorMessage}
          </Text>
        )}
      </label>
    );
  },
);

InputField.displayName = 'InputField';

export default InputField;
